/**
 * JobScheduleLauncher · iter303ak-fix30 — Work Booked · T10 · Action 10
 * ─────────────────────────────────────────────────────────────────────
 * Inline "Work Schedule Review" panel that shows every prebooked calendar
 * entry for this parent appraisal and lets the operator edit dates,
 * work amounts (hours/days), notes — or add missing bookings.
 *
 * Prior to fix30 this was a deep-link placeholder that opened /calendar
 * in a new tab. That behaviour is preserved via the "Open full calendar"
 * button on the right — but the primary UX is now inline review + edit.
 *
 * Continue is enabled once every accepted child has a booking with a
 * scheduled_start.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CalendarClock, CalendarDays, Clock, StickyNote, ExternalLink, Plus, Trash2, Save, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUSES = [
  { key: 'prebooked', label: 'Prebooked', active: 'bg-slate-600 text-white border-slate-700', inactive: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50' },
  { key: 'confirmed', label: 'Confirmed', active: 'bg-emerald-600 text-white border-emerald-700', inactive: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
];

function bookingToRow(b) {
  const isHours = !!b.expected_work_hours;
  return {
    _id: b._id,
    child_appraisal_id: b.child_appraisal_id || '',
    child_appraisal_no: b.child_appraisal_no || null,
    quote_type_ref: b.quote_type_ref || '',
    variant_label: b.variant_label || '',
    scheduled_start: b.scheduled_start || '',
    scheduled_end: b.scheduled_end || '',
    work_amount: isHours ? Number(b.expected_work_hours) : Number(b.expected_work_days || 1),
    work_unit: isHours ? 'hours' : 'days',
    notes: b.notes || '',
    status: b.status || 'prebooked',
    dirty: false,
  };
}

export default function JobScheduleLauncher({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.appraisal_record?.child_decisions
      || capturedData?.appraisal?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [globalReviewed, setGlobalReviewed] = useState(!!value?.reviewed);

  const load = useCallback(async () => {
    if (!parentId) { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/calendar/bookings?parent_appraisal_id=${parentId}`);
      setRows((data || []).map(bookingToRow));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load bookings');
    } finally { setLoading(false); }
  }, [parentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onChange) onChange({ reviewed: globalReviewed, row_count: rows.length });
  }, [globalReviewed, rows.length]);   

  const patchRow = (id, patch) => setRows(prev => prev.map(r => r._id === id ? { ...r, ...patch, dirty: true } : r));

  const openCalendar = () => window.open(parentId ? `/calendar?appraisal_id=${parentId}` : '/calendar', '_blank', 'noopener,noreferrer');

  // Which accepted children still have no booking?
  const missingChildren = useMemo(() => {
    const covered = new Set(rows.map(r => r.child_appraisal_id).filter(Boolean));
    return acceptedChildren.filter(c => !covered.has(c.child_id));
  }, [acceptedChildren, rows]);

  const addBookingForChild = async (child) => {
    setSaving(true); setErr('');
    try {
      const payload = {
        parent_appraisal_id: parentId,
        child_appraisal_id: child.child_id,
        child_appraisal_no: child.appraisal_no || null,
        quote_type_ref: child.quote_type_ref || '',
        variant_label: child.variant_label || '',
        scheduled_start: '',
        expected_work_days: 1,
        expected_work_hours: 0,
        status: 'prebooked',
      };
      const { data } = await api.post('/calendar/bookings/upsert-for-child', payload);
      setRows(prev => [...prev, bookingToRow(data)]);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Add failed');
    } finally { setSaving(false); }
  };

  const saveRow = async (id) => {
    const r = rows.find(x => x._id === id);
    if (!r) return;
    if (!r.scheduled_start) { setErr('Scheduled start required.'); return; }
    setSaving(true); setErr('');
    try {
      const isHours = r.work_unit === 'hours';
      const amount = Math.max(Number(r.work_amount || 1), 0.5);
      const workDays = isHours ? Math.max(Math.ceil(amount / 8), 1) : Math.max(Math.round(amount), 1);
      const workHours = isHours ? Math.round(amount) : 0;
      const { data } = await api.patch(`/calendar/bookings/${id}`, {
        scheduled_start: r.scheduled_start,
        expected_work_days: workDays,
        expected_work_hours: workHours,
        notes: r.notes || '',
        status: r.status || 'prebooked',
      });
      setRows(prev => prev.map(x => x._id === id ? bookingToRow(data) : x));
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveAllDirty = async () => {
    const dirty = rows.filter(r => r.dirty);
    if (!dirty.length) return;
    for (const r of dirty) {
       
      await saveRow(r._id);
    }
  };

  const removeRow = async (id) => {
    setSaving(true); setErr('');
    try {
      await api.delete(`/calendar/bookings/${id}`);
      setRows(prev => prev.filter(x => x._id !== id));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally { setSaving(false); }
  };

  const allBooked = rows.length > 0 && missingChildren.length === 0 && rows.every(r => r.scheduled_start);
  const canContinue = allBooked && globalReviewed;

  return (
    <div className="space-y-4" data-testid="wf-work-schedule-review">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Work Schedule Review</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Every accepted quote from the Accepted step was auto-prebooked. Adjust dates, work time and notes below — or add a missing booking.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
              data-testid="wf-wsr-refresh"
              className="px-2.5 py-1 rounded text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={openCalendar}
              data-testid="wf-wsr-open-calendar"
              className="px-2.5 py-1 rounded text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
              <ExternalLink size={12} /> Full calendar
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading bookings…</div>}

      {!loading && rows.length === 0 && missingChildren.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted children on this appraisal. Complete the Accepted step first.
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r._id} className={`p-3 border rounded-lg ${r.dirty ? 'bg-amber-50/60 border-amber-300' : 'bg-white border-slate-200'}`}
              data-testid={`wf-wsr-row-${r._id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-sm font-medium text-slate-800">
                  {[r.quote_type_ref, r.variant_label].filter(Boolean).join(' · ') || 'Booking'}
                </span>
                {r.child_appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{r.child_appraisal_no}</span>}
                <div className="flex items-center gap-1 ml-auto">
                  {STATUSES.map(s => {
                    const active = r.status === s.key;
                    return (
                      <button key={s.key} type="button"
                        onClick={() => patchRow(r._id, { status: s.key })}
                        data-testid={`wf-wsr-status-${r._id}-${s.key}`}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${active ? s.active : s.inactive}`}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="pl-3 border-l-2 border-slate-200 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <CalendarDays className="w-3 h-3 inline mr-1 -mt-0.5" /> Scheduled start <span className="text-rose-500">*</span>
                  </label>
                  <input type="date" value={r.scheduled_start || ''}
                    onChange={(e) => patchRow(r._id, { scheduled_start: e.target.value })}
                    data-testid={`wf-wsr-date-${r._id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
                  {r.scheduled_end && (
                    <div className="text-[10px] text-slate-500 mt-0.5">ends {r.scheduled_end}</div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <Clock className="w-3 h-3 inline mr-1 -mt-0.5" /> Amount
                  </label>
                  <input type="number" step="1" min="0.5" value={r.work_amount ?? 1}
                    onChange={(e) => patchRow(r._id, { work_amount: e.target.value })}
                    data-testid={`wf-wsr-amount-${r._id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Unit</label>
                  <div className="flex rounded border border-slate-300 overflow-hidden">
                    {['hours', 'days'].map(u => (
                      <button key={u} type="button"
                        onClick={() => patchRow(r._id, { work_unit: u })}
                        data-testid={`wf-wsr-unit-${r._id}-${u}`}
                        className={`flex-1 px-2 py-1 text-[11px] font-semibold ${r.work_unit === u ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <StickyNote className="w-3 h-3 inline mr-1 -mt-0.5" /> Notes
                  </label>
                  <input type="text" value={r.notes || ''}
                    onChange={(e) => patchRow(r._id, { notes: e.target.value })}
                    placeholder="access, parking, key-holder…"
                    data-testid={`wf-wsr-notes-${r._id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs" />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-end gap-2">
                <button onClick={() => removeRow(r._id)} disabled={saving}
                  data-testid={`wf-wsr-remove-${r._id}`}
                  className="px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 rounded inline-flex items-center gap-1 disabled:opacity-40">
                  <Trash2 size={11} /> Remove
                </button>
                <button onClick={() => saveRow(r._id)} disabled={saving || !r.dirty || !r.scheduled_start}
                  data-testid={`wf-wsr-save-${r._id}`}
                  className="px-3 py-1 rounded-md text-[11px] font-semibold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-1">
                  <Save size={11} /> {r.dirty ? 'Save row' : 'Saved'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {missingChildren.length > 0 && (
        <div className="p-3 border border-dashed border-amber-300 bg-amber-50/40 rounded-lg space-y-2">
          <div className="text-xs font-semibold text-amber-800">
            {missingChildren.length} accepted quote(s) without a booking yet
          </div>
          {missingChildren.map(c => (
            <div key={c.child_id} className="flex items-center justify-between p-2 bg-white border border-amber-200 rounded">
              <div className="text-xs text-slate-800">
                {[c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ')}
                {c.appraisal_no && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{c.appraisal_no}</span>}
              </div>
              <button onClick={() => addBookingForChild(c)} disabled={saving}
                data-testid={`wf-wsr-add-${c.child_id}`}
                className="px-2 py-1 rounded text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-1 disabled:opacity-40">
                <Plus size={11} /> Add booking
              </button>
            </div>
          ))}
        </div>
      )}

      {rows.some(r => r.dirty) && (
        <button onClick={saveAllDirty} disabled={saving}
          data-testid="wf-wsr-save-all"
          className="w-full px-3 py-2 rounded-md text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-40 inline-flex items-center justify-center gap-2">
          <Save size={14} /> Save all changed rows
        </button>
      )}

      <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded cursor-pointer hover:bg-slate-50">
        <input type="checkbox" checked={globalReviewed} onChange={(e) => setGlobalReviewed(e.target.checked)}
          data-testid="wf-wsr-reviewed"
          className="w-5 h-5" />
        <CheckCircle2 size={16} className={globalReviewed ? 'text-emerald-600' : 'text-slate-300'} />
        <span className="text-sm font-medium text-slate-700">
          {globalReviewed ? 'Schedule reviewed & confirmed for this stage' : 'Tick once the schedule looks right for every quote'}
        </span>
      </label>

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      {savedAt && !err && !rows.some(r => r.dirty) && (
        <div className="text-xs text-slate-500">Last saved {new Date(savedAt).toLocaleString()}</div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!canContinue}
            data-testid="wf-wsr-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
